/**
 * "Configuration health" gadget (Ship): from the app configuration (shared with Jobs and Open work) the secrets a
 * service declares and nobody set, the variables at least two services set but others do not (often one forgotten),
 * the scheduled jobs switched off, and the services waiting for a restart to apply a change. All fine: one line.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { configHealth } from '../../../utils/homeGadgets/ship';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useAppConfig } from './sources';

export default function ConfigHealthGadget({ projectId, onOpenConfig = null, onOpenJobs = null }) {
  const { data, error, loading } = useAppConfig(projectId);
  const health = configHealth({ overview: data?.overview, details: data?.details || {} });
  const issues = health.secrets.length + health.partialVars.length + health.paused.length + health.restart.length;
  return (
    <GadgetCard
      links={<><TabLink onClick={onOpenConfig}>Configuration</TabLink><TabLink onClick={onOpenJobs}>Jobs</TabLink></>}
      meta={data && issues > 0 ? `${issues} to look at` : null}
      testId="config-health"
      title="Configuration health"
    >
      <GadgetBody
        data={data}
        empty={issues === 0}
        emptyText={(data?.overview?.services || []).length === 0 ? 'No service to configure yet.' : 'Every secret is set, the variables match and no job is paused.'}
        error={error}
        errorText="The configuration could not be read just now."
        loading={loading}
        loadingText="Reading the configuration…"
      >
        {() => (
          <>
            <ul className="gd-rows">
              {health.secrets.map((s) => (
                <li className="gd-row" data-testid={`cfg-secret-${s.service}-${s.name}`} key={`s-${s.service}-${s.name}`}>
                  <span className="gd-bad">Secret to set</span><strong>{s.name}</strong><span className="ph-muted">{s.service}</span>
                </li>
              ))}
              {health.partialVars.map((v) => (
                <li className="gd-row" data-testid={`cfg-var-${v.name}`} key={`v-${v.name}`}>
                  <span className="gd-warn">Only some</span><strong>{v.name}</strong><span className="gd-grow ph-muted">in {v.in.join(', ')}, not in {v.missing.join(', ')}</span>
                </li>
              ))}
              {health.paused.map((j) => (
                <li className="gd-row" data-testid={`cfg-paused-${j.service}-${j.name}`} key={`j-${j.service}-${j.name}`}>
                  <span className="gd-warn">Paused job</span><strong>{j.name}</strong><span className="ph-muted">{j.service}</span>
                </li>
              ))}
              {health.restart.map((service) => (
                <li className="gd-row" key={`r-${service}`}><span className="gd-warn">Restart to apply</span><strong>{service}</strong></li>
              ))}
            </ul>
            {data?.missing?.length > 0 && <p className="gd-note">{data.missing.join(', ')}: could not be read just now, not counted.</p>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

ConfigHealthGadget.propTypes = { onOpenConfig: PropTypes.func, onOpenJobs: PropTypes.func, projectId: PropTypes.string.isRequired };
